import type { TextLanguage } from '@/hooks/use-locale'

import { shouldShowNonFsdsNavItems } from '@/utils/domain-branding-resolver'

import { NavItem } from '@/types/nav.type'

/** Default admin landing while Overview (`/admin/dashboard`) is hidden. */
export const ADMIN_DEFAULT_HREF = '/admin/courses'

const baseAdminNavItems: NavItem[] = [
  // Temporarily hidden — restore when Overview is ready again
  // {
  //   title: 'Tổng quan',
  //   href: '/admin/dashboard',
  //   icon: 'chart',
  //   label: 'dashboard',
  //   role: 'admin',
  //   domainVisibility: 'all',
  // },
  {
    title: 'Khóa học',
    href: ADMIN_DEFAULT_HREF,
    icon: 'library',
    label: 'courses',
    role: 'admin',
    domainVisibility: 'all',
  },
  {
    title: 'Online Tests',
    href: '/admin/online-tests',
    icon: 'clipboard',
    label: 'online-tests',
    role: 'ownerAdminInstructor',
    domainVisibility: 'non-fsds-only',
  },
  {
    title: 'AI Generated Content',
    href: '/admin/ai-generated-content',
    icon: 'brain',
    label: 'ai-generated-content',
    role: 'generatedContent',
    // flag ai-generated-content for local
    domainVisibility: 'non-fsds-only',
  },
  {
    title: 'Bài mẫu',
    href: '/admin/example-practices',
    icon: 'notebookPen',
    label: 'example-practices',
    role: 'ownerAdminInstructor',
    domainVisibility: 'non-fsds-only',
  },
  {
    title: 'Người dùng',
    href: '/admin/users',
    icon: 'users',
    label: 'users',
    role: 'admin',
    domainVisibility: 'all',
  },
  {
    title: 'Sandbox',
    href: '/admin/sandboxes',
    icon: 'server',
    label: 'sandbox',
    role: 'admin',
    domainVisibility: 'fsds-only',
  },
  {
    title: 'Blog',
    href: '/admin/blogs',
    icon: 'notebookPen',
    label: 'blogs',
    role: 'admin',
    domainVisibility: 'all',
  },
  {
    title: 'Webinars',
    href: '/admin/webinars',
    icon: 'tvMinimalPlay',
    label: 'webinars',
    role: 'admin',
    domainVisibility: 'all',
  },
  {
    title: 'Tài nguyên',
    href: '/resource',
    icon: 'folder',
    label: 'resources',
    role: 'admin',
    domainVisibility: 'all',
  },
  {
    title: 'Chứng chỉ',
    href: '/admin/certificate-config',
    icon: 'award',
    label: 'certificate-config',
    role: 'ownerOrAdmin',
    domainVisibility: 'non-fsds-only',
  },
  {
    title: 'Hall of Fame',
    href: '/admin/hall-of-fames',
    icon: 'appWindow',
    label: 'hall-of-fame',
    role: 'admin',
    domainVisibility: 'all',
  },
  {
    title: 'Khảo sát',
    href: '/admin/surveys',
    icon: 'server',
    label: 'surveys',
    role: 'admin',
    domainVisibility: 'fsds-only',
  },
  {
    title: 'Vouchers',
    href: '/admin/voucher',
    icon: 'factory',
    label: 'voucher',
    role: 'admin',
    domainVisibility: 'fsds-only',
  },
  {
    title: 'Tuyển dụng',
    href: '/admin/jobs',
    icon: 'userSearch',
    label: 'jobs',
    role: 'all',
    domainVisibility: 'fsds-only',
  },
  {
    title: 'Công ty tuyển dụng',
    href: '/admin/companies',
    icon: 'building',
    label: 'companies',
    role: 'admin',
    domainVisibility: 'fsds-only',
  },
  {
    title: 'Announcements',
    href: '/admin/send-notification',
    icon: 'send',
    label: 'send-notification',
    role: 'ownerOrAdmin',
    domainVisibility: 'all',
  },
  {
    title: 'Emoji',
    href: '/admin/emoji',
    icon: 'emoji',
    label: 'emoji',
    role: 'ownerOrAdmin',
    domainVisibility: 'all',
  },
  {
    title: 'Credit Points',
    href: '/admin/credit-points',
    icon: 'creditCard',
    label: 'credit-points',
    role: 'admin',
    domainVisibility: 'fsds-only',
  },
  {
    title: 'Cấu hình thông báo',
    href: '/admin/notification-config',
    icon: 'bell',
    label: 'notification-config',
    role: 'admin',
    domainVisibility: 'fsds-only',
  },
  {
    title: 'Cài đặt',
    href: '/admin/media-defaults',
    icon: 'settings',
    label: 'media-defaults',
    role: 'ownerLevel',
    domainVisibility: 'non-fsds-only',
  },
  {
    title: 'Hỗ trợ',
    href: 'mailto:support@edunodes.com',
    icon: 'support',
    label: 'support',
    role: 'admin',
    domainVisibility: 'non-fsds-only',
    pin: 'bottom',
    external: true,
  },
]

/**
 * Filter nav items by domain visibility.
 * Items without domainVisibility or with 'all' are always included.
 */
export function filterNavItemsByDomain(
  items: NavItem[],
  isFsdsDomain: boolean,
  hostname?: string | null
): NavItem[] {
  return items.filter(item => {
    const visibility = item.domainVisibility ?? 'all'
    if (visibility === 'all') return true
    if (visibility === 'fsds-only') return isFsdsDomain
    if (visibility === 'non-fsds-only') return shouldShowNonFsdsNavItems(isFsdsDomain, hostname)
    return true
  })
}

/**
 * Get admin nav items filtered by domain visibility.
 * @param isFsdsDomain - true if the current domain is FSDS, false otherwise.
 *                        When omitted, all items are returned (backward-compatible).
 */
export function getAdminNavItems(isFsdsDomain?: boolean, hostname?: string | null): NavItem[] {
  if (isFsdsDomain === undefined) return baseAdminNavItems
  return filterNavItemsByDomain(baseAdminNavItems, isFsdsDomain, hostname)
}

export const adminNavItems = baseAdminNavItems

export const dashboardNavItems: NavItem[] = [
  {
    title: 'Online Tests',
    href: '/online-tests',
    icon: 'clipboard',
    label: 'online-tests',
    role: 'user',
    domainVisibility: 'non-fsds-only',
  },
  {
    title: 'Tuyển dụng',
    href: '/dashboard/jobs',
    icon: 'userSearch',
    label: 'jobs',
    role: 'all',
    domainVisibility: 'fsds-only',
  },
  {
    title: 'Công ty tuyển dụng',
    href: '/dashboard/companies',
    icon: 'building',
    label: 'companies',
    role: 'all',
    domainVisibility: 'fsds-only',
  },
  {
    title: 'Quản lý bài viết',
    href: '/dashboard/blogs',
    icon: 'notebookPen',
    label: 'blogs',
    role: 'all',
    domainVisibility: 'fsds-only',
  },
]

/**
 * Get dashboard nav items filtered by domain visibility.
 */
export function getDashboardNavItems(isFsdsDomain?: boolean, hostname?: string | null): NavItem[] {
  if (isFsdsDomain === undefined) return dashboardNavItems
  return filterNavItemsByDomain(dashboardNavItems, isFsdsDomain, hostname)
}

export function localizeNavItems(items: NavItem[], text: TextLanguage): NavItem[] {
  const titleByLabel: Record<string, string> = {
    courses: text.NAV.COURSES,
    'online-tests': text.NAV.ONLINE_TESTS,
    'ai-generated-content': text.NAV.AI_GENERATED_CONTENT,
    'example-practices': text.NAV.EXAMPLE_PRACTICES,
    users: text.NAV.USERS,
    sandbox: text.NAV.SANDBOX,
    blogs: text.NAV.BLOGS,
    webinars: text.NAV.WEBINARS,
    resources: text.NAV.RESOURCES,
    'certificate-config': text.NAV.CERTIFICATE_CONFIG,
    'hall-of-fame': text.NAV.HALL_OF_FAME,
    surveys: text.NAV.SURVEYS,
    voucher: text.NAV.VOUCHERS,
    jobs: text.NAV.JOBS,
    companies: text.NAV.COMPANIES,
    'send-notification': text.NAV.ANNOUNCEMENTS,
    emoji: text.NAV.EMOJI,
    'credit-points': text.NAV.CREDIT_POINTS,
    'notification-config': text.NAV.NOTIFICATION_CONFIG,
    'media-defaults': text.NAV.SETTINGS,
    support: text.NAV.SUPPORT,
  }

  return items.map(item => {
    // Dashboard blog management uses the same `blogs` label with a different title intent
    if (item.label === 'blogs' && item.href?.startsWith('/dashboard/blogs')) {
      return { ...item, title: text.NAV.BLOG_MANAGEMENT }
    }
    const localizedTitle = item.label ? titleByLabel[item.label] : undefined
    return localizedTitle ? { ...item, title: localizedTitle } : item
  })
}
